import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react';

import { Box, Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import { NotificationsAnnouncements } from '@rover/rsdk/src/apiClient/latest';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import useNotificationsCenterShim from '@rover/shared/js/hooks/useNotificationsCenterShim';
import { SCROLL_AREA_CLASSNAME } from '@rover/shared/js/ssr/BaseTemplate/NavBar/NotificationsCenter/constants';

import LoadingSpinner from '../../typography/LoadingSpinner';
import NotificationsCenterRow from '../NotificationsCenterRow';

import NoNotifications from './NoNotifications';

export type Props = {
  isMobileBreakpoint?: boolean;
  notifications?: NotificationsAnnouncements[];
  language?: string;
  isLoading?: boolean;
  onMessageClick?: (url: string) => void;
  onMessageDismiss?: (url: string) => void;
  onMessageRead?: (url: string) => void;
  onRequestLoadMoreNotifications?: () => void;
};

const INFINITE_SCROLL_BUFFER = 100;
const HEADER_HEIGHT = '32px';

export const NotificationsCenterList: FC<Props> = ({
  isMobileBreakpoint,
  notifications,
  language,
  isLoading,
  onMessageClick,
  onMessageDismiss,
  onMessageRead,
  onRequestLoadMoreNotifications,
}) => {
  const theme = useTheme();
  const listRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLElement | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const [allowRequestMore, setAllowRequestMore] = useState(true);

  const handleScroll = useCallback(() => {
    const { current: currentListRef } = listRef;
    const { current: currentScrollRef } = scrollRef;

    if (currentListRef && currentScrollRef) {
      const { offsetHeight: listHeight } = currentListRef;
      const { scrollTop, offsetHeight } = currentScrollRef;

      for (let i = 0; i < currentListRef.children.length; i += 1) {
        const listEl = currentListRef.children[i] as HTMLLIElement;
        const bottomOfRowFromTopOfList = listEl.offsetTop + listEl.offsetHeight;
        // bottomRelativity is negative when bottom of the particular Row is above bottom of List
        const bottomRelativity = bottomOfRowFromTopOfList - scrollTop - offsetHeight;
        const halfNotificationHeight = listEl.offsetHeight / 2;

        // if not read and at least 50% in view mark as read
        if (notifications && notifications.length > i) {
          const notification = notifications[i];

          if (notification) {
            const isRead = notification.read;

            if (!isRead && bottomRelativity <= halfNotificationHeight && onMessageRead) {
              onMessageRead(notification.opk);
            }
          }
        }
      }

      if (allowRequestMore && listHeight - (offsetHeight + scrollTop) < INFINITE_SCROLL_BUFFER) {
        setAllowRequestMore(false);
        if (onRequestLoadMoreNotifications) onRequestLoadMoreNotifications();
      }
    }
  }, [
    listRef,
    scrollRef,
    allowRequestMore,
    setAllowRequestMore,
    notifications,
    onMessageRead,
    onRequestLoadMoreNotifications,
  ]);

  useEffect(() => {
    if (isMobileBreakpoint && wrapperRef.current) {
      const { top } = wrapperRef.current.getBoundingClientRect();
      wrapperRef.current.style.height = `calc(100vh - ${top}px)`;
    }
  }, [isMobileBreakpoint]);

  useEffect(() => {
    if (!isLoading) {
      setAllowRequestMore(true);
    }
  }, [isLoading]);

  useEffect(() => {
    handleScroll();
  }, [notifications?.length, handleScroll]);

  return (
    <Flex ref={wrapperRef} flexDirection="column" width="100%" height="100%">
      <Flex
        height={HEADER_HEIGHT}
        paddingLeft="4x"
        alignItems="center"
        borderBottom={`1px solid ${theme.colors.border.primary.toString()}`}
      >
        <Text size="100" textColor="tertiary" textTransform="uppercase" fontWeight="semibold">
          <Trans id="notifications" />
        </Text>
      </Flex>

      {notifications?.length ? (
        <Box
          className={SCROLL_AREA_CLASSNAME}
          ref={scrollRef}
          onScroll={handleScroll}
          position="relative"
          height={`calc(100% - ${HEADER_HEIGHT})`}
          sx={{ overflowY: 'auto' }}
        >
          <Flex
            as="ul"
            flexDirection="column"
            listStyleType="none"
            padding="0x"
            margin="0x"
            ref={listRef}
          >
            {notifications.map(
              (notification) =>
                !notification.isDismissed && (
                  <NotificationsCenterRow
                    key={notification.opk}
                    notification={notification}
                    language={language}
                    onClick={onMessageClick}
                    onDismiss={onMessageDismiss}
                  />
                )
            )}
          </Flex>

          {isLoading && (
            <Flex
              justifyContent="center"
              alignItems="center"
              background="primary"
              height="8x"
              width="100%"
              textAlign="center"
            >
              <LoadingSpinner />
            </Flex>
          )}
        </Box>
      ) : (
        <NoNotifications />
      )}
    </Flex>
  );
};

const NotificationsCenterListContainer: FC<{ isMobileBreakpoint?: boolean }> = ({
  isMobileBreakpoint,
}) => {
  const notificationsCenterListData = useNotificationsCenterShim();
  const { language } = useFrontendConfigurationShim();

  return (
    <NotificationsCenterList
      isMobileBreakpoint={isMobileBreakpoint}
      language={language}
      {...notificationsCenterListData}
    />
  );
};

export default NotificationsCenterListContainer;
